import React, { FC, useContext, useCallback } from 'react';
import classNames from 'classnames';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import Notes from 'components/common/Notes';

import NotesPreview from './NotesPreview';
import { AllTimelineGqlOptions, useTimeline } from '../TimelineContext';
import { EditingNotesContext, TimelineNotesContext } from './TimelineNotesContext';

import { useModal } from 'vibo-ui/Modal';
import { onError } from 'graphql/helpers';

import { Modals } from 'types/enums';
import { ConfirmActionModalProps } from 'components/modals/ConfirmActionModal';

import useStyles from './style';

const SectionNotes: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const editingContext = useContext(EditingNotesContext);
  const {
    variables,
    sections: { mutations },
  } = useTimeline<AllTimelineGqlOptions>();
  const { section } = useContext(TimelineNotesContext);

  const [updateSection] = useMutation(mutations.updateSection.mutation, {
    onError,
    refetchQueries: mutations.updateSection.refetchQ,
  });

  const isNoteChanged = section.note !== editingContext.notesText;

  const { openModal } = useModal();

  const openConfirmSavingModal = useCallback(
    () =>
      openModal<ConfirmActionModalProps>({
        key: Modals.confirmAction,
        props: {
          title: t('saveNotes'),
          children: t('notesChangesHaventBeenSaved'),
          submit: {
            text: t('saveChanges'),
            onClick: () => {
              updateSection({
                variables: {
                  ...variables,
                  payload: {
                    note: editingContext?.notesText,
                  },
                },
                update: discardChanges,
              });
            },
          },
          discard: {
            text: t('dontSave'),
            onClick: discardChanges,
          },
        },
      }),
    [variables, editingContext?.notesText]
  );

  const cancelEditing = useCallback(() => editingContext?.setIsEditing(false), []);

  const discardChanges = useCallback(() => {
    cancelEditing();
  }, []);

  return (
    <>
      {editingContext?.isEditing ? (
        <div
          onClick={isNoteChanged ? openConfirmSavingModal : cancelEditing}
          className={classes.savingOverlay}
        />
      ) : null}
      {editingContext?.isEditing ? (
        <Notes
          onChange={editingContext.setNotesText}
          className={classNames({
            [classes.invisible]: !editingContext?.isEditing,
          })}
          initialValue={section?.note || ''}
          autoFocus={editingContext?.isEditing}
          placeholder={`${t('addNotesAbout')} ${section.name}...`}
        />
      ) : (
        <NotesPreview />
      )}
    </>
  );
};

export default SectionNotes;
